import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { createFileRoute, Link, Outlet, useRouterState, useNavigate } from '@tanstack/react-router';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { PageLayout } from '../components/layout/PageLayout';

import { useProjectsStore } from '../store/useProjectsStore';
import { useUserStore } from '../store/useUserStore';
import { useAuth } from '../hooks/useAuth';
import {
  useGetProjectTicketSales,
  useGetProject,
  useUpdateProject,
  mapApiProjectToProject,
  useGetProjectMembers,
  useGetProjectInvitations,
} from '../api/projects';
import { useGetTasks, useCreateTask, useUpdateTask, useDeleteTask } from '../api/tasks';

import type { ApiTask, ApiTaskStatus, ApiTaskPriority } from '../api/tasks';
import { FiInfo, FiShield } from 'react-icons/fi';

import { TaskDetailsModal } from '../components/projects/modals/TaskDetailsModal';
import { UpdateStatusModal } from '../components/projects/modals/UpdateStatusModal';
import { EditTaskModal } from '../components/projects/modals/EditTaskModal';
import { AddTaskModal } from '../components/projects/modals/AddTaskModal';
import { ScheduleEntryModal } from '../components/projects/modals/ScheduleEntryModal';
import { DeleteScheduleEntryModal } from '../components/projects/modals/DeleteScheduleEntryModal';
import { DeleteTaskModal } from '../components/projects/modals/DeleteTaskModal';
import { toast } from '../utils/toast';
import { uploadFilesToCloudinary } from '../utils/cloudinary';
import { extractApiError } from '../utils/apiError';
import { useGetProfileEvents } from '../api/events';
import type { ApiRunOfShowEntry } from '../api/runOfShow';
import {
  useGetRunOfShowEntries,
  useCreateRunOfShowEntry,
  useUpdateRunOfShowEntry,
  useDeleteRunOfShowEntry,
} from '../api/runOfShow';
import { exportRunOfShowPdf } from '../utils/exportRunOfShowPdf';
import {
  wallClockFromUtcIso,
  wallClockAsPicked,
  formatTimeOnly,
  formatTimezoneName,
} from '../utils/eventDate';
import { DEFAULT_TIMEZONE } from '../components/ui/TimezoneSelect';

import { ProjectHeaderBanner } from '../components/projects/ProjectHeaderBanner';
import { ProjectOverviewTab } from '../components/projects/tabs/ProjectOverviewTab';
import { ProjectTasksTab } from '../components/projects/tabs/ProjectTasksTab';
import { ProjectBudgetTab } from '../components/projects/tabs/ProjectBudgetTab';
import { ProjectRolesTab } from '../components/projects/tabs/ProjectRolesTab';
import { ProjectInventoryTab } from '../components/projects/tabs/ProjectInventoryTab';
import {
  useGetInventoryItems,
  useCreateInventoryItem,
  useUpdateInventoryItem,
  useDeleteInventoryItem,
  useGetInventoryImportCandidates,
  useImportInventoryItems,
  mapUiCategoryToApi,
  mapApiCategoryToUi,
  mapUiConditionToApi,
  mapApiConditionToUi,
} from '../api/inventory';
import { ProjectRunOfShowTab } from '../components/projects/tabs/ProjectRunOfShowTab';
import { ProjectActivityTab } from '../components/projects/tabs/ProjectActivityTab';
import { ProjectTicketSalesTab } from '../components/projects/tabs/ProjectTicketSalesTab';

import { AddBudgetCategoryModal } from '../components/projects/modals/AddBudgetCategoryModal';
import { EditBudgetCategoryModal } from '../components/projects/modals/EditBudgetCategoryModal';
import { AddTransactionModal } from '../components/projects/modals/AddTransactionModal';
import { AddInventoryItemModal } from '../components/projects/modals/AddInventoryItemModal';
import { EditInventoryItemModal } from '../components/projects/modals/EditInventoryItemModal';
import { DeleteInventoryItemModal } from '../components/projects/modals/DeleteInventoryItemModal';
import { ChangeLinkedEventModal } from '../components/projects/modals/ChangeLinkedEventModal';
import { ImportAccountInventoryModal } from '../components/projects/modals/ImportAccountInventoryModal';

export const Route = createFileRoute('/projects/$projectId')({
  component: ProjectDetailWrapper,
});

function ProjectDetailWrapper() {
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;
  console.log('ProjectDetailWrapper rendering for pathname:', pathname);
  const isChildActive =
    pathname.toLowerCase().includes('/edit') || pathname.toLowerCase().includes('/notifications');

  if (isChildActive) {
    return <Outlet />;
  }
  return <ProjectDetailPage />;
}

type TabType =
  | 'Overview'
  | 'Ticket Sales'
  | 'Roles'
  | 'Tasks'
  | 'Budget'
  | 'Inventory'
  | 'Run of Show'
  | 'Activity';

const formatEventDateLabel = (start?: string): string => {
  if (!start) return '';
  const d = new Date(start);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// ─── Roles tab: real member/invitation data → the mock CrewMember row shape ─

const ROLE_DISPLAY_LABEL: Record<string, string> = {
  manager: 'Manager',
  editor: 'Editor',
  guest: 'Guest',
};

const CREW_AVATAR_COLORS = [
  'bg-blue-600',
  'bg-emerald-600',
  'bg-purple-600',
  'bg-pink-600',
  'bg-amber-600',
  'bg-rose-600',
  'bg-teal-600',
];

const crewAvatarColor = (id: number) =>
  CREW_AVATAR_COLORS[Math.abs(id) % CREW_AVATAR_COLORS.length];

interface MemberUserLike {
  full_name?: string | null;
  username?: string | null;
  email?: string;
}

const memberUserDisplayName = (u: MemberUserLike) =>
  u.full_name?.trim() || u.username?.trim() || u.email || 'User';

const memberUserInitials = (u: MemberUserLike) => {
  const name = memberUserDisplayName(u);
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const formatInviteDate = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const mapApiStatusToLocal = (status: string): 'To Do' | 'In Progress' | 'Complete' => {
  if (status === 'to_do' || status === 'todo') return 'To Do';
  if (status === 'in_progress' || status === 'inprogress') return 'In Progress';
  if (status === 'complete' || status === 'completed') return 'Complete';
  return 'To Do';
};

const mapApiPriorityToLocal = (priority: string): 'Low' | 'Medium' | 'High' => {
  if (priority === 'high') return 'High';
  if (priority === 'medium') return 'Medium';
  if (priority === 'low') return 'Low';
  return 'Medium';
};

const mapApiTaskToLocalTask = (apiTask: ApiTask): any => {
  const firstAssignee = apiTask.assignees?.[0];
  const assigneeName = firstAssignee ? memberUserDisplayName(firstAssignee) : 'Unassigned';
  const assigneeInitials = firstAssignee ? memberUserInitials(firstAssignee) : 'UN';
  const assigneeColor = firstAssignee ? crewAvatarColor(firstAssignee.id) : 'bg-zinc-700';

  const alerts: ('Overdue' | 'Due Soon')[] = [];
  if (apiTask.end_date) {
    const dueDateObj = new Date(apiTask.end_date + 'T23:59:59');
    const now = new Date();
    if (apiTask.status !== 'complete') {
      if (dueDateObj < now) {
        alerts.push('Overdue');
      } else {
        const diffTime = Math.abs(dueDateObj.getTime() - now.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays <= 2) {
          alerts.push('Due Soon');
        }
      }
    }
  }

  let dueDateStr = '';
  if (apiTask.end_date) {
    const parts = apiTask.end_date.split('-');
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const d = parseInt(parts[2], 10);
    if (y && m && d) {
      dueDateStr = new Date(y, m - 1, d).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    }
  }

  return {
    id: apiTask.uuid,
    projectId: apiTask.project_uuid,
    title: apiTask.title,
    priority: mapApiPriorityToLocal(apiTask.priority),
    status: mapApiStatusToLocal(apiTask.status),
    assignee: {
      id: firstAssignee?.id,
      name: assigneeName,
      initials: assigneeInitials,
      color: assigneeColor,
    },
    assignees: apiTask.assignees,
    created_by: apiTask.created_by,
    created_by_id: (apiTask as any).created_by_id,
    dueDate: dueDateStr || 'No due date',
    alerts,
    description: apiTask.description,
    externalLink: apiTask.external_link,
    coverImage: apiTask.cover_image,
    attachments: apiTask.attachments,
    rawTask: apiTask,
  };
};

// Helper to parse date string robustly in local time to avoid timezone offset issues and Safari bugs
const parseToLocalDate = (dateStr: string): Date => {
  try {
    if (!dateStr) return new Date();
    // Try to match standard YYYY-MM-DD
    const ymdMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (ymdMatch) {
      const year = parseInt(ymdMatch[1], 10);
      const month = parseInt(ymdMatch[2], 10) - 1;
      const day = parseInt(ymdMatch[3], 10);
      return new Date(year, month, day);
    }
    // Try to match "18 Jun 2026" or similar formats
    const cleanStr = dateStr.replace(',', '').trim();
    const parts = cleanStr.split(/\s+/);
    if (parts.length >= 3) {
      const day = parseInt(parts[0], 10);
      const monthStr = parts[1].toLowerCase();
      const year = parseInt(parts[2], 10);
      const months: Record<string, number> = {
        jan: 0,
        feb: 1,
        mar: 2,
        apr: 3,
        may: 4,
        jun: 5,
        jul: 6,
        aug: 7,
        sep: 8,
        oct: 9,
        nov: 10,
        dec: 11,
      };
      const month = months[monthStr.substring(0, 3)];
      if (month !== undefined && !isNaN(day) && !isNaN(year)) {
        let hours = 0;
        let minutes = 0;
        if (parts[3]) {
          const timeParts = parts[3].split(':');
          hours = parseInt(timeParts[0], 10) || 0;
          minutes = parseInt(timeParts[1], 10) || 0;
        }
        return new Date(year, month, day, hours, minutes);
      }
    }
    // Fallback: replace space with T for Safari compatibility if it contains a space and dash
    const cleanIso =
      dateStr.includes(' ') && dateStr.includes('-') ? dateStr.replace(' ', 'T') : dateStr;
    const d = new Date(cleanIso);
    if (!isNaN(d.getTime())) {
      return new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes());
    }
    return new Date();
  } catch {
    return new Date();
  }
};

// ─── Run of Show helpers ───────────────────────────────────────────────────

const groupScheduleByDate = (
  entries: ApiRunOfShowEntry[],
): Array<{ dateKey: string; dateLabel: string; entries: ApiRunOfShowEntry[] }> => {
  const groups = new Map<string, ApiRunOfShowEntry[]>();
  entries.forEach((entry) => {
    // Times are stored "as picked" (UTC-stamped), so the date is read straight from the UTC fields.
    const { date } = wallClockFromUtcIso(entry.start_datetime);
    if (!groups.has(date)) groups.set(date, []);
    groups.get(date)!.push(entry);
  });
  return Array.from(groups.entries()).map(([dateKey, groupEntries]) => {
    const first = groupEntries[0];
    const dateLabel = new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(first.start_datetime));
    return { dateKey, dateLabel, entries: groupEntries };
  });
};

const formatScheduleDuration = (startIso: string, endIso: string): string => {
  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();
  if (isNaN(start) || isNaN(end) || end <= start) return '';
  const totalMinutes = Math.round((end - start) / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h`;
  return `${minutes} min`;
};

const formatDateTimeForTable = (isoString: string) => {
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return isoString;
    const day = d.getDate();
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${day} ${month} ${year} ${hours}:${minutes} ${ampm}`;
  } catch {
    return isoString;
  }
};

function ProjectDetailPage() {
  const { projectId } = Route.useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate({ to: '/sign-in', search: { redirect: `/projects/${projectId}` } });
    }
  }, [isAuthenticated, navigate, projectId]);

  const {
    tasks,
    crewMembers,
    milestones,
    budgetCategories,
    transactions,
    ticketTransactions,
    activities,
    addTask,
    toggleTaskStatus: storeToggleTaskStatus,
    deleteTask: storeDeleteTask,
    addBudgetCategory,
    addTransaction,
    updateBudgetCategory,
  } = useProjectsStore();

  const { user } = useUserStore();
  const { data: apiProject, isLoading: isProjectLoading } = useGetProject(projectId);
  const { mutate: updateProjectMutation } = useUpdateProject();

  const { data: apiTasksResponse } = useGetTasks(
    {
      projectUuid: projectId || '',
    },
    {
      enabled: !!projectId,
    },
  );

  const { data: apiInventoryResponse, isLoading: isInventoryLoading } = useGetInventoryItems(
    {
      projectUuid: projectId || '',
    },
    {
      enabled: !!projectId,
    },
  );

  const { mutate: createInventoryItemMutation, isPending: isCreatingInventory } =
    useCreateInventoryItem();
  const { mutate: updateInventoryItemMutation, isPending: isUpdatingInventory } =
    useUpdateInventoryItem();
  const { mutate: deleteInventoryItemMutation, isPending: isDeletingInventory } =
    useDeleteInventoryItem();

  const { data: importCandidatesResponse, isLoading: isImportCandidatesLoading } =
    useGetInventoryImportCandidates(
      projectId || '',
      { page: 1, page_size: 100 },
      { enabled: !!projectId },
    );

  const { mutate: importInventoryItemsMutation, isPending: isImportingInventory } =
    useImportInventoryItems();

  const apiTasks = apiTasksResponse?.results ?? [];

  const { mutate: createTaskMutation } = useCreateTask();
  const { mutate: updateTaskMutation } = useUpdateTask();
  const { mutate: deleteTaskMutation } = useDeleteTask();

  const project = useMemo(
    () => (apiProject ? mapApiProjectToProject(apiProject, user?.id) : undefined),
    [apiProject, user?.id],
  );

  const projectInventory = useMemo(() => {
    if (!project) return [];
    return (apiInventoryResponse?.results ?? []).map((item) => ({
      id: item.uuid,
      projectId: item.project_uuid,
      name: item.name,
      category: mapApiCategoryToUi(item.category),
      condition: mapApiConditionToUi(item.condition),
      assetTag: item.asset_tag,
      budgetAllocation: item.budget_allocation,
      quantity: 1,
      status: 'Available',
    }));
  }, [apiInventoryResponse, project]);

  // Mock inventory seeding has been replaced with the REST API integration.
  const canArchive = project?.permissions ? project.permissions.can_archive : false;

  const toggleTaskStatus = (taskId: string) => {
    if (isGuestUser) {
      toast.error('Guests can only view tasks.');
      return;
    }
    const localTask = projectTasks.find((t) => t.id === taskId);
    const apiTask = apiTasks.find((t) => t.uuid === taskId);
    const targetTask = localTask || apiTask;

    if (targetTask && !canUserUpdateTask(targetTask)) {
      toast.error('You do not have permission to update this task.');
      return;
    }

    const statusDisplayNames: Record<string, string> = {
      to_do: 'To Do',
      in_progress: 'In Progress',
      complete: 'Complete',
      'To Do': 'In Progress',
      'In Progress': 'Complete',
      Complete: 'To Do',
    };

    if (apiTask && project?.uuid) {
      const nextStatusMap: Record<ApiTaskStatus, ApiTaskStatus> = {
        to_do: 'in_progress',
        in_progress: 'complete',
        complete: 'to_do',
      };
      const nextStatus = nextStatusMap[apiTask.status] || 'to_do';
      const targetLabel = statusDisplayNames[nextStatus] || 'To Do';
      updateTaskMutation(
        {
          projectUuid: project.uuid,
          taskUuid: taskId,
          payload: { status: nextStatus },
        },
        {
          onSuccess: () => {
            toast.success(`Task status changed to ${targetLabel}`);
          },
        },
      );
    } else {
      const currentTask = projectTasks.find((t) => t.id === taskId);
      const nextLabel = currentTask
        ? statusDisplayNames[currentTask.status] || 'In Progress'
        : 'In Progress';
      storeToggleTaskStatus(taskId);
      toast.success(`Task status changed to ${nextLabel}`);
    }
  };

  const setTaskStatus = (taskId: string, targetStatus: 'To Do' | 'In Progress' | 'Complete') => {
    if (isGuestUser) {
      toast.error('Guests can only view tasks.');
      return;
    }
    const localTask = projectTasks.find((t) => t.id === taskId);
    const apiTask = apiTasks.find((t) => t.uuid === taskId);
    const targetTask = localTask || apiTask;

    if (targetTask && !canUserUpdateTask(targetTask)) {
      toast.error('You do not have permission to update this task.');
      return;
    }

    if (apiTask && project?.uuid) {
      const apiStatusMap: Record<'To Do' | 'In Progress' | 'Complete', ApiTaskStatus> = {
        'To Do': 'to_do',
        'In Progress': 'in_progress',
        Complete: 'complete',
      };
      updateTaskMutation(
        {
          projectUuid: project.uuid,
          taskUuid: taskId,
          payload: { status: apiStatusMap[targetStatus] },
        },
        {
          onSuccess: () => {
            toast.success(`Task status changed to ${targetStatus}`);
          },
        },
      );
    } else {
      useProjectsStore.setState((state) => ({
        tasks: state.tasks.map((t) => (t.id === taskId ? { ...t, status: targetStatus } : t)),
      }));
      toast.success(`Task status changed to ${targetStatus}`);
    }
  };

  const deleteTask = (taskId: string) => {
    if (isGuestUser) {
      toast.error('Guests can only view tasks.');
      return;
    }
    const apiTask = apiTasks.find((t) => t.uuid === taskId);
    setDeletingTaskId(taskId);
    if (apiTask && project?.uuid) {
      deleteTaskMutation(
        {
          projectUuid: project.uuid,
          taskUuid: taskId,
        },
        {
          onSuccess: () => {
            setDeletingTaskId(null);
            setTaskToDelete(null);
            setDetailModalTask((current: any) => (current?.id === taskId ? null : current));
          },
          onError: () => {
            setDeletingTaskId(null);
          },
        },
      );
    } else {
      storeDeleteTask(taskId);
      setDeletingTaskId(null);
      setTaskToDelete(null);
      setDetailModalTask((current: any) => (current?.id === taskId ? null : current));
      toast.success('Task deleted successfully');
    }
  };

  const handleUnarchive = () => {
    if (!project) return;
    if (!canArchive) {
      toast.error('Editors can only archive projects they created.');
      return;
    }
    updateProjectMutation({
      projectUuid: project.id,
      payload: { status: 'active' },
    });
  };

  // Active events for the "Change Linked Event" picker — same profile-events
  // endpoint used on the profile page's events tab, filtered to active only.
  const { data: activeEventsData } = useGetProfileEvents(user?.id, 1, 50, true);
  const linkableEvents = useMemo(
    () =>
      (activeEventsData?.results ?? []).map((e) => ({
        id: e.event_uuid || String(e.id),
        title: e.event_title || 'Untitled Event',
        date: formatEventDateLabel(e.start),
        type: e.event_type || '',
      })),
    [activeEventsData],
  );

  const [activeTab, setActiveTab] = useState<TabType>('Overview');

  // Modal control states
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [detailModalTask, setDetailModalTask] = useState<any>(null);
  const [statusModalTask, setStatusModalTask] = useState<any>(null);
  const [taskToDelete, setTaskToDelete] = useState<any>(null);
  const [selectedStatus, setSelectedStatus] = useState<'To Do' | 'In Progress' | 'Complete'>(
    'To Do',
  );
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isTxModalOpen, setIsTxModalOpen] = useState(false);

  const handleConfirmUpdateStatus = () => {
    if (!statusModalTask) return;
    if (isGuestUser) {
      toast.error('Guests can only view tasks.');
      return;
    }
    if (!canUserUpdateTask(statusModalTask)) {
      toast.error('You do not have permission to update this task.');
      return;
    }
    setTaskStatus(statusModalTask.id, selectedStatus);
    setStatusModalTask(null);
  };

  // Form states
  // 1. Task Form
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [taskLink, setTaskLink] = useState('');
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [coverFileName, setCoverFileName] = useState('');
  const [attachmentFileName, setAttachmentFileName] = useState('');
  const [taskStartDate, setTaskStartDate] = useState('');
  const [taskEndDate, setTaskEndDate] = useState('');
  const [taskAssigneeId, setTaskAssigneeId] = useState<string>('');
  const [taskQuantity, setTaskQuantity] = useState('1');
  const [taskBudgetAllocation, setTaskBudgetAllocation] = useState('');
  const [taskCreatePriority, setTaskCreatePriority] = useState<'Low' | 'Medium' | 'High'>('Medium');
  const [taskCreateStatus, setTaskCreateStatus] = useState<
    'To Do' | 'In Progress' | 'Blocked' | 'Complete'
  >('To Do');

  // 1b. Edit Task Form
  const [editingTask, setEditingTask] = useState<any>(null);
  const [editTaskTitle, setEditTaskTitle] = useState('');
  const [editTaskDescription, setEditTaskDescription] = useState('');
  const [editTaskLink, setEditTaskLink] = useState('');
  const [editCoverFile, setEditCoverFile] = useState<File | null>(null);
  const [editAttachmentFile, setEditAttachmentFile] = useState<File | null>(null);
  const [editCoverFileName, setEditCoverFileName] = useState('');
  const [editAttachmentFileName, setEditAttachmentFileName] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editAssigneeId, setEditAssigneeId] = useState<string>('');
  const [editQuantity, setEditQuantity] = useState('1');
  const [editBudgetAllocation, setEditBudgetAllocation] = useState('');
  const [editPriority, setEditPriority] = useState<'High' | 'Medium' | 'Low'>('Medium');
  const [editStatus, setEditStatus] = useState<'To Do' | 'In Progress' | 'Blocked' | 'Complete'>(
    'To Do',
  );
  const [isTaskSubmitting, setIsTaskSubmitting] = useState(false);
  const [deletingTaskId, setDeletingTaskId] = useState<string | null>(null);

  // Task form errors & validators
  const [taskErrors, setTaskErrors] = useState<Record<string, string | undefined>>({});
  const [editTaskErrors, setEditTaskErrors] = useState<Record<string, string | undefined>>({});

  const validateAddTask = (): boolean => {
    const errs: Record<string, string | undefined> = {};
    let hasRequiredMissing = false;

    if (!taskTitle.trim()) {
      errs.title = 'Task title is required.';
      hasRequiredMissing = true;
    }

    if (taskLink.trim()) {
      const urlPattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/.*)?$/i;
      if (!urlPattern.test(taskLink.trim())) {
        errs.externalLink = 'Please enter a valid URL (e.g. https://example.com).';
      }
    }

    if (taskStartDate && taskEndDate) {
      if (new Date(taskEndDate) < new Date(taskStartDate)) {
        errs.endDate = 'End date cannot be earlier than start date.';
      }
    }

    if (taskQuantity.trim()) {
      const q = parseInt(taskQuantity, 10);
      if (isNaN(q) || q < 1) {
        errs.quantity = 'Quantity must be at least 1.';
      }
    }

    if (taskBudgetAllocation.trim()) {
      const b = parseFloat(taskBudgetAllocation);
      if (isNaN(b) || b < 0) {
        errs.budgetAllocation = 'Budget allocation must be a valid amount.';
      }
    }

    if (coverFile && coverFile.size > 5 * 1024 * 1024) {
      errs.coverFile = 'Cover image file size must be 5 MB or smaller.';
    }

    if (attachmentFile && attachmentFile.size > 20 * 1024 * 1024) {
      errs.attachmentFile = 'Attachment file size must be 20 MB or smaller.';
    }

    setTaskErrors(errs);

    if (hasRequiredMissing) {
      toast.error('Please fill in all required fields.');
    } else if (Object.keys(errs).length > 0) {
      toast.error('Please correct the highlighted form errors.');
    }

    return Object.keys(errs).length === 0;
  };

  const validateEditTask = (): boolean => {
    const errs: Record<string, string | undefined> = {};
    let hasRequiredMissing = false;

    if (!editTaskTitle.trim()) {
      errs.title = 'Task title is required.';
      hasRequiredMissing = true;
    }

    if (editTaskLink.trim()) {
      const urlPattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/.*)?$/i;
      if (!urlPattern.test(editTaskLink.trim())) {
        errs.externalLink = 'Please enter a valid URL (e.g. https://example.com).';
      }
    }

    if (editStartDate && editEndDate) {
      if (new Date(editEndDate) < new Date(editStartDate)) {
        errs.endDate = 'End date cannot be earlier than start date.';
      }
    }

    if (editQuantity.trim()) {
      const q = parseInt(editQuantity, 10);
      if (isNaN(q) || q < 1) {
        errs.quantity = 'Quantity must be at least 1.';
      }
    }

    if (editBudgetAllocation.trim()) {
      const b = parseFloat(editBudgetAllocation);
      if (isNaN(b) || b < 0) {
        errs.budgetAllocation = 'Budget allocation must be a valid amount.';
      }
    }

    if (editCoverFile && editCoverFile.size > 5 * 1024 * 1024) {
      errs.coverFile = 'Cover image file size must be 5 MB or smaller.';
    }

    if (editAttachmentFile && editAttachmentFile.size > 20 * 1024 * 1024) {
      errs.attachmentFile = 'Attachment file size must be 20 MB or smaller.';
    }

    setEditTaskErrors(errs);

    if (hasRequiredMissing) {
      toast.error('Please fill in all required fields.');
    } else if (Object.keys(errs).length > 0) {
      toast.error('Please correct the highlighted form errors.');
    }

    return Object.keys(errs).length === 0;
  };

  const openEditTaskModal = (task: any) => {
    if (isGuestUser) {
      toast.error('Guests can only view tasks.');
      return;
    }
    if (!canUserUpdateTask(task)) {
      toast.error('You do not have permission to update this task.');
      return;
    }
    setEditingTask(task);
    setEditTaskTitle(task.title || '');
    setEditTaskDescription(task.description || '');
    setEditTaskLink(task.externalLink || '');
    setEditCoverFileName(task.coverImage ? 'Existing Cover Image' : '');
    setEditAttachmentFileName(task.attachments?.[0] ? 'Existing Attachment' : '');
    setEditCoverFile(null);
    setEditAttachmentFile(null);
    setEditStartDate(task.startDate || '');
    setEditEndDate(
      task.rawTask?.end_date ||
        (task.dueDate && task.dueDate !== 'No due date' && task.dueDate !== 'No Date'
          ? task.dueDate
          : ''),
    );
    setEditAssigneeId(task.assignee?.id ? String(task.assignee.id) : '');
    setEditQuantity(task.quantity ? String(task.quantity) : '1');
    setEditBudgetAllocation(task.budgetAllocation || '');
    setEditPriority(task.priority || 'Medium');
    setEditStatus(
      task.status === 'In Progress'
        ? 'In Progress'
        : task.status === 'Complete'
          ? 'Complete'
          : 'To Do',
    );
    setEditTaskErrors({});
  };

  const handleEditTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isGuestUser) {
      toast.error('Guests can only view tasks.');
      return;
    }
    if (editingTask && !canUserUpdateTask(editingTask)) {
      toast.error('You do not have permission to update this task.');
      return;
    }
    if (!editingTask || !validateEditTask() || isTaskSubmitting) return;

    setIsTaskSubmitting(true);
    let uploadedCoverUrl: string | undefined = undefined;
    let uploadedAttachmentUrls: string[] | undefined = undefined;

    try {
      if (editCoverFile) {
        const results = await uploadFilesToCloudinary([editCoverFile], {
          folder: 'task_images',
          uploadPreset: 'service_preset',
        });
        if (results[0]?.secure_url) {
          uploadedCoverUrl = results[0].secure_url;
        }
      }

      if (editAttachmentFile) {
        const results = await uploadFilesToCloudinary([editAttachmentFile], {
          folder: 'task_attachments',
          uploadPreset: 'service_preset',
        });
        if (results[0]?.secure_url) {
          uploadedAttachmentUrls = [results[0].secure_url];
        }
      }
    } catch (err) {
      console.error('Cloudinary upload error:', err);
      toast.error('Failed to upload file to Cloudinary. Please try again.');
      setIsTaskSubmitting(false);
      return;
    }

    const priorityMap: Record<'Low' | 'Medium' | 'High', ApiTaskPriority> = {
      Low: 'low',
      Medium: 'medium',
      High: 'high',
    };

    const statusMap: Record<'To Do' | 'In Progress' | 'Blocked' | 'Complete', ApiTaskStatus> = {
      'To Do': 'to_do',
      'In Progress': 'in_progress',
      Blocked: 'to_do',
      Complete: 'complete',
    };

    if (project?.uuid) {
      const payload: any = {
        title: editTaskTitle,
        description: editTaskDescription,
        external_link: editTaskLink,
        priority: priorityMap[editPriority],
        status: statusMap[editStatus],
        start_date: editStartDate || null,
        end_date: editEndDate || null,
        quantity: parseInt(editQuantity) || 1,
        budget_allocation: editBudgetAllocation || '0.00',
      };

      if (editAssigneeId) {
        payload.assignee_ids = [Number(editAssigneeId)];
      }

      if (uploadedCoverUrl !== undefined) {
        payload.cover_image = uploadedCoverUrl;
      }
      if (uploadedAttachmentUrls !== undefined) {
        payload.attachments = uploadedAttachmentUrls;
      }

      updateTaskMutation(
        {
          projectUuid: project.uuid,
          taskUuid: editingTask.id,
          payload,
        },
        {
          onSuccess: () => {
            setEditingTask(null);
            setEditCoverFile(null);
            setEditAttachmentFile(null);
            setEditTaskErrors({});
            setIsTaskSubmitting(false);
            toast.success('Task updated successfully');
          },
          onError: () => {
            setIsTaskSubmitting(false);
          },
        },
      );
    } else {
      useProjectsStore.setState((state) => ({
        tasks: state.tasks.map((t) =>
          t.id === editingTask.id
            ? {
                ...t,
                title: editTaskTitle,
                description: editTaskDescription || undefined,
                externalLink: editTaskLink || undefined,
                priority: editPriority,
                status:
                  editStatus === 'In Progress'
                    ? 'In Progress'
                    : editStatus === 'Complete'
                      ? 'Complete'
                      : 'To Do',
                ...(uploadedCoverUrl ? { coverImage: uploadedCoverUrl } : {}),
                ...(uploadedAttachmentUrls ? { attachments: uploadedAttachmentUrls } : {}),
              }
            : t,
        ),
      }));
      setEditingTask(null);
      setEditCoverFile(null);
      setEditAttachmentFile(null);
      setEditTaskErrors({});
      setIsTaskSubmitting(false);
      toast.success('Task updated successfully');
    }
  };

  const [catName, setCatName] = useState('');
  const [catAllocated, setCatAllocated] = useState('10000');
  const [catNotes, setCatNotes] = useState('');

  // 3. Transaction Form
  const [txType, setTxType] = useState<'Expense' | 'Revenue'>('Expense');
  const [txTitle, setTxTitle] = useState('');
  const [txCategory, setTxCategory] = useState('');
  const [txAmount, setTxAmount] = useState('1000');
  const [txDate, setTxDate] = useState('2026-07-07');
  const [txNotes, setTxNotes] = useState('');

  // 2b. Edit Budget Category Form
  const [isEditBudgetModalOpen, setIsEditBudgetModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [catEditName, setCatEditName] = useState('');
  const [catEditAllocated, setCatEditAllocated] = useState('');
  const [catEditNotes, setCatEditNotes] = useState('');

  // Task Filter state (Tasks Tab)
  const [taskFilter, setTaskFilter] = useState<'All' | 'To Do' | 'In Progress' | 'Complete'>('All');

  // 5. Inventory Form States (Add, Edit, Delete, Import)
  const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [itemName, setItemName] = useState('');
  const [itemCategory, setItemCategory] = useState('');
  const [itemAssetTag, setItemAssetTag] = useState('');
  const [itemQuantity, setItemQuantity] = useState('1');
  const [itemBudgetAllocation, setItemBudgetAllocation] = useState('');
  const [itemLocation, setItemLocation] = useState('');
  const [itemCondition, setItemCondition] = useState<
    'Excellent' | 'Good' | 'Fair' | 'Needs Repair'
  >('Excellent');

  // Edit Inventory Modal
  const [isEditInventoryModalOpen, setIsEditInventoryModalOpen] = useState(false);
  const [isEditCategoryDropdownOpen, setIsEditCategoryDropdownOpen] = useState(false);
  const [editingInventoryItem, setEditingInventoryItem] = useState<any>(null);
  const [editItemName, setEditItemName] = useState('');
  const [editItemCategory, setEditItemCategory] = useState('');
  const [editItemAssetTag, setEditItemAssetTag] = useState('');
  const [editItemQuantity, setEditItemQuantity] = useState('1');
  const [editItemBudgetAllocation, setEditItemBudgetAllocation] = useState('');
  const [editItemLocation, setEditItemLocation] = useState('');
  const [editItemCondition, setEditItemCondition] = useState<
    'Excellent' | 'Good' | 'Fair' | 'Needs Repair'
  >('Excellent');

  // Delete Inventory Confirmation Modal
  const [isDeleteInventoryModalOpen, setIsDeleteInventoryModalOpen] = useState(false);
  const [deletingInventoryItem, setDeletingInventoryItem] = useState<any>(null);

  // Import Inventory Modal
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importSearchQuery, setImportSearchQuery] = useState('');
  const [selectedImportIds, setSelectedImportIds] = useState<string[]>([]);

  const [inventoryCreateError, setInventoryCreateError] = useState('');
  const [inventoryEditError, setInventoryEditError] = useState('');

  useEffect(() => {
    if (!isInventoryModalOpen) {
      setInventoryCreateError('');
    }
  }, [isInventoryModalOpen]);

  useEffect(() => {
    if (!isEditInventoryModalOpen) {
      setInventoryEditError('');
    }
  }, [isEditInventoryModalOpen]);

  // 6. Schedule Form (Run of Show)
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [editingScheduleEntry, setEditingScheduleEntry] = useState<ApiRunOfShowEntry | null>(null);
  const [deletingScheduleEntryId, setDeletingScheduleEntryId] = useState<string | null>(null);
  const [schTitle, setSchTitle] = useState('');
  const [schStartDate, setSchStartDate] = useState('');
  const [schStartTime, setSchStartTime] = useState('');
  const [schEndDate, setSchEndDate] = useState('');
  const [schEndTime, setSchEndTime] = useState('');
  const [schTimezone, setSchTimezone] = useState(DEFAULT_TIMEZONE);
  const [schLocation, setSchLocation] = useState('');
  const [schAssigneeIds, setSchAssigneeIds] = useState<string[]>([]);
  const [schNotes, setSchNotes] = useState('');
  const [scheduleErrors, setScheduleErrors] = useState<Record<string, string | undefined>>({});

  // Ticket Sales Filter States
  const [ticketFilter, setTicketFilter] = useState<string>('All');
  const [ticketSearch, setTicketSearch] = useState('');
  const [sortBy, setSortBy] = useState<'Newest' | 'Oldest' | 'Highest Amount' | 'Lowest Amount'>(
    'Newest',
  );

  // Custom Popover States
  const [activePopover, setActivePopover] = useState<
    'ticket' | 'sort' | 'filter' | 'dateRange' | 'amountSpent' | null
  >(null);

  // Advanced Filter states
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [minSpend, setMinSpend] = useState('');
  const [maxSpend, setMaxSpend] = useState('');
  const [includeRefunded, setIncludeRefunded] = useState(true);
  const [vipOnly, setVipOnly] = useState(false);
  const [page, setPage] = useState(1);

  // Reset pagination to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [ticketFilter, ticketSearch, dateFrom, dateTo, minSpend, maxSpend, includeRefunded]);

  // Temporary Filter states (uncommitted until Apply is clicked)
  const [tempDateFrom, setTempDateFrom] = useState('');
  const [tempDateTo, setTempDateTo] = useState('');
  const [tempMinSpend, setTempMinSpend] = useState('');
  const [tempMaxSpend, setTempMaxSpend] = useState('');
  const [tempVipOnly, setTempVipOnly] = useState(false);

  // Change Linked Event Modal States
  const [isChangeEventModalOpen, setIsChangeEventModalOpen] = useState(false);
  const [selectedEventUuid, setSelectedEventUuid] = useState('');
  const [searchEventQuery, setSearchEventQuery] = useState('');

  const filteredEvents = useMemo(() => {
    return linkableEvents.filter((e) =>
      e.title.toLowerCase().includes(searchEventQuery.toLowerCase()),
    );
  }, [linkableEvents, searchEventQuery]);

  const projectTicketTransactions = useMemo(() => {
    return project ? ticketTransactions.filter((t) => t.projectId === project.id) : [];
  }, [ticketTransactions, project]);

  // Project members — fetched for permissions and member selection
  const { data: projectMembersData } = useGetProjectMembers(
    {
      projectUuid: project?.uuid || '',
      page_size: 100,
    },
    { enabled: !!project?.uuid },
  );

  // Derive guest status dynamically from project details & project members
  const isGuestUser = useMemo(() => {
    if (apiProject?.permissions?.can_edit === false) {
      return true;
    }
    const membersList = (apiProject?.members || projectMembersData?.results) as
      | Array<{
          user?: { id?: number | string };
          id?: number | string;
          role?: string;
        }>
      | undefined;

    if (membersList && user?.id) {
      const selfMember = membersList.find((m) => {
        const memberUserId = m.user?.id ?? m.id;
        return String(memberUserId) === String(user.id);
      });
      if (selfMember && selfMember.role === 'guest') {
        return true;
      }
    }
    return false;
  }, [
    apiProject?.permissions?.can_edit,
    apiProject?.members,
    projectMembersData?.results,
    user?.id,
  ]);

  const canUserUpdateTask = useCallback(
    (task: any): boolean => {
      if (!task) return false;
      if (isGuestUser) return false;

      const isProjectOwner =
        project?.isMyProject ||
        (apiProject?.created_by_id && String(apiProject.created_by_id) === String(user?.id)) ||
        apiProject?.permissions?.can_delete === true;

      const membersList = (apiProject?.members || projectMembersData?.results || crewMembers) as
        | Array<any>
        | undefined;

      let memberRole: string | undefined;
      if (membersList && user?.id) {
        const selfMember = membersList.find((m) => {
          const memberUserId = m.user?.id ?? m.id;
          return String(memberUserId) === String(user.id);
        });
        if (selfMember) {
          memberRole = selfMember.role;
        }
      }

      const isOwnerOrManager =
        isProjectOwner ||
        memberRole === 'owner' ||
        memberRole === 'manager' ||
        memberRole === 'Project Owner';

      if (isOwnerOrManager) {
        return true;
      }

      const taskCreatedById =
        task.created_by?.id ??
        task.created_by_id ??
        task.createdById ??
        (typeof task.created_by === 'number' || typeof task.created_by === 'string'
          ? task.created_by
          : undefined);

      const isCreator =
        taskCreatedById !== undefined &&
        user?.id !== undefined &&
        String(taskCreatedById) === String(user.id);

      if (isCreator) return true;

      let isAssignee = false;
      const currentUserId = user?.id !== undefined ? String(user.id) : undefined;
      const currentUserEmail = user?.email?.toLowerCase();
      const currentUserName = (
        user?.fullName ||
        (user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user?.name) ||
        user?.username ||
        ''
      ).toLowerCase();

      if (Array.isArray(task.assignees)) {
        isAssignee = task.assignees.some((a: any) => {
          if (!a) return false;
          if (currentUserId && String(a.id || a.user_id) === currentUserId) return true;
          if (currentUserEmail && a.email && a.email.toLowerCase() === currentUserEmail)
            return true;
          if (currentUserName && a.name && a.name.toLowerCase() === currentUserName) return true;
          if (currentUserName && a.full_name && a.full_name.toLowerCase() === currentUserName)
            return true;
          if (currentUserName && a.username && a.username.toLowerCase() === currentUserName)
            return true;
          return false;
        });
      }

      if (!isAssignee && task.assignee) {
        if (currentUserId && task.assignee.id && String(task.assignee.id) === currentUserId) {
          isAssignee = true;
        } else if (
          currentUserEmail &&
          task.assignee.email &&
          task.assignee.email.toLowerCase() === currentUserEmail
        ) {
          isAssignee = true;
        } else if (
          currentUserName &&
          task.assignee.name &&
          task.assignee.name.toLowerCase() === currentUserName
        ) {
          isAssignee = true;
        }
      }

      if (!isAssignee && Array.isArray(task.assignee_ids) && currentUserId) {
        isAssignee = task.assignee_ids.some((id: any) => String(id) === currentUserId);
      }

      if (!isAssignee && task.assigneeId && currentUserId) {
        if (String(task.assigneeId) === currentUserId) {
          isAssignee = true;
        }
      }

      return isAssignee;
    },
    [
      isGuestUser,
      project?.isMyProject,
      apiProject?.created_by_id,
      apiProject?.permissions?.can_delete,
      apiProject?.members,
      projectMembersData?.results,
      crewMembers,
      user,
    ],
  );

  const canUserDeleteTask = useCallback(
    (task: any): boolean => {
      if (!task) return false;
      if (isGuestUser) return false;

      const isProjectOwner =
        project?.isMyProject ||
        (apiProject?.created_by_id && String(apiProject.created_by_id) === String(user?.id)) ||
        apiProject?.permissions?.can_delete === true;

      const membersList = (apiProject?.members || projectMembersData?.results || crewMembers) as
        | Array<any>
        | undefined;

      let memberRole: string | undefined;
      if (membersList && user?.id) {
        const selfMember = membersList.find((m) => {
          const memberUserId = m.user?.id ?? m.id;
          return String(memberUserId) === String(user.id);
        });
        if (selfMember) {
          memberRole = selfMember.role;
        }
      }

      const isOwnerOrManager =
        isProjectOwner ||
        memberRole === 'owner' ||
        memberRole === 'manager' ||
        memberRole === 'Project Owner';

      if (isOwnerOrManager) {
        return true;
      }

      const taskCreatedById =
        task.created_by?.id ??
        task.created_by_id ??
        task.createdById ??
        (typeof task.created_by === 'number' || typeof task.created_by === 'string'
          ? task.created_by
          : undefined);

      const isCreator =
        taskCreatedById !== undefined &&
        user?.id !== undefined &&
        String(taskCreatedById) === String(user.id);

      return isCreator;
    },
    [
      isGuestUser,
      project?.isMyProject,
      apiProject?.created_by_id,
      apiProject?.permissions?.can_delete,
      apiProject?.members,
      projectMembersData?.results,
      crewMembers,
      user,
    ],
  );

  // Editors may only modify schedule entries they created (or are assigned to);
  // owners/managers retain full access. Mirrors canUserUpdateTask/canUserDeleteTask
  // above so schedule permissions stay consistent with the Tasks module.
  const canUserUpdateScheduleEntry = useCallback(
    (entry: ApiRunOfShowEntry | null | undefined): boolean => {
      if (!entry) return false;
      if (isGuestUser) return false;

      const isProjectOwner =
        project?.isMyProject ||
        (apiProject?.created_by_id && String(apiProject.created_by_id) === String(user?.id)) ||
        apiProject?.permissions?.can_delete === true;

      const membersList = (apiProject?.members || projectMembersData?.results || crewMembers) as
        | Array<any>
        | undefined;

      let memberRole: string | undefined;
      if (membersList && user?.id) {
        const selfMember = membersList.find((m) => {
          const memberUserId = m.user?.id ?? m.id;
          return String(memberUserId) === String(user.id);
        });
        if (selfMember) {
          memberRole = selfMember.role;
        }
      }

      const isOwnerOrManager =
        isProjectOwner ||
        memberRole === 'owner' ||
        memberRole === 'manager' ||
        memberRole === 'Project Owner';

      if (isOwnerOrManager) return true;

      const isCreator =
        entry.created_by?.id !== undefined &&
        user?.id !== undefined &&
        String(entry.created_by.id) === String(user.id);

      if (isCreator) return true;

      const currentUserId = user?.id !== undefined ? String(user.id) : undefined;
      return (
        !!currentUserId &&
        Array.isArray(entry.assignees) &&
        entry.assignees.some((a) => String(a.id) === currentUserId)
      );
    },
    [
      isGuestUser,
      project?.isMyProject,
      apiProject?.created_by_id,
      apiProject?.permissions?.can_delete,
      apiProject?.members,
      projectMembersData?.results,
      crewMembers,
      user,
    ],
  );

  const canUserDeleteScheduleEntry = useCallback(
    (entry: ApiRunOfShowEntry | null | undefined): boolean => {
      if (!entry) return false;
      if (isGuestUser) return false;

      const isProjectOwner =
        project?.isMyProject ||
        (apiProject?.created_by_id && String(apiProject.created_by_id) === String(user?.id)) ||
        apiProject?.permissions?.can_delete === true;

      const membersList = (apiProject?.members || projectMembersData?.results || crewMembers) as
        | Array<any>
        | undefined;

      let memberRole: string | undefined;
      if (membersList && user?.id) {
        const selfMember = membersList.find((m) => {
          const memberUserId = m.user?.id ?? m.id;
          return String(memberUserId) === String(user.id);
        });
        if (selfMember) {
          memberRole = selfMember.role;
        }
      }

      const isOwnerOrManager =
        isProjectOwner ||
        memberRole === 'owner' ||
        memberRole === 'manager' ||
        memberRole === 'Project Owner';

      if (isOwnerOrManager) return true;

      return (
        entry.created_by?.id !== undefined &&
        user?.id !== undefined &&
        String(entry.created_by.id) === String(user.id)
      );
    },
    [
      isGuestUser,
      project?.isMyProject,
      apiProject?.created_by_id,
      apiProject?.permissions?.can_delete,
      apiProject?.members,
      projectMembersData?.results,
      crewMembers,
      user,
    ],
  );

  const promptDeleteTask = (task: any) => {
    if (!task) return;
    if (isGuestUser) {
      toast.error('Guests can only view tasks.');
      return;
    }
    if (!canUserDeleteTask(task)) {
      toast.error('You do not have permission to delete this task.');
      return;
    }
    setTaskToDelete(task);
  };

  const handleConfirmDeleteTask = () => {
    if (!taskToDelete) return;
    deleteTask(taskToDelete.id);
  };
  const { data: projectInvitationsData } = useGetProjectInvitations(
    {
      projectUuid: project?.uuid || '',
      page_size: 100,
    },
    { enabled: activeTab === 'Roles' },
  );

  // Ticket sales: on-demand fetch only when tab is active, and skipped entirely for guests
  const canViewTicketSales = !isGuestUser;
  const { data: ticketSalesData, isLoading: isSalesLoading } = useGetProjectTicketSales(
    {
      projectUuid: project?.uuid || '',
      search: ticketSearch || undefined,
      ticket_type: ticketFilter !== 'All' ? ticketFilter : undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      amount_min: minSpend ? parseFloat(minSpend) : undefined,
      amount_max: maxSpend ? parseFloat(maxSpend) : undefined,
      include_refunded: includeRefunded,
      page: page,
      page_size: 10,
    },
    { enabled: activeTab === 'Ticket Sales' && canViewTicketSales },
  );

  const ticketTypesSummary = useMemo(() => {
    if (project?.uuid) {
      return (ticketSalesData?.summary?.ticket_types ?? []).map((t) => ({
        ticketType: t.ticket_type,
        quantity: t.quantity,
        revenue: parseFloat(String(t.revenue ?? 0)),
      }));
    } else {
      const uniqueTypes = Array.from(new Set(projectTicketTransactions.map((tx) => tx.ticketType)));
      return uniqueTypes.map((type) => {
        const txsForType = projectTicketTransactions.filter((tx) => tx.ticketType === type);
        const quantity = txsForType.reduce((acc, tx) => acc + (tx.quantity || 1), 0);
        const revenue = txsForType.reduce((acc, tx) => acc + tx.amount, 0);
        return {
          ticketType: type,
          quantity,
          revenue,
        };
      });
    }
  }, [project?.uuid, ticketSalesData?.summary?.ticket_types, projectTicketTransactions]);

  const parsedLiveTransactions = useMemo(() => {
    if (!project?.uuid || !ticketSalesData?.results) return [];

    return ticketSalesData.results.map((item, idx) => ({
      id: `live-${idx}`,
      projectId: project?.id || '',
      customerName: item.customer_name,
      customerEmail: item.customer_email,
      ticketType: item.ticket_type as 'Regular' | 'VIP',
      paymentMethod: (item.payment_method === 'free_booking' ? 'Free booking' : 'Stripe') as
        | 'Stripe'
        | 'Free booking',
      amount: parseFloat(String(item.revenue)),
      quantity: item.quantity,
      dateTime: formatDateTimeForTable(item.purchased_at),
      rawDateTime: item.purchased_at,
    }));
  }, [project?.id, project?.uuid, ticketSalesData?.results]);

  const mockFilteredTransactions = useMemo(() => {
    return projectTicketTransactions.filter((tx) => {
      // 1. Ticket type filter (from Ticket Dropdown)
      if (ticketFilter !== 'All' && tx.ticketType !== ticketFilter) return false;

      // 2. Search query filter
      if (ticketSearch.trim() !== '') {
        const q = ticketSearch.toLowerCase();
        if (
          !tx.customerName.toLowerCase().includes(q) &&
          !tx.customerEmail.toLowerCase().includes(q)
        ) {
          return false;
        }
      }

      // 3. Date Range filter
      if (dateFrom) {
        const txDate = parseToLocalDate(tx.dateTime);
        const fromDate = parseToLocalDate(dateFrom);
        if (txDate.getTime() < fromDate.getTime()) return false;
      }
      if (dateTo) {
        const txDate = parseToLocalDate(tx.dateTime);
        const toDate = parseToLocalDate(dateTo);
        toDate.setHours(23, 59, 59, 999);
        if (txDate.getTime() > toDate.getTime()) return false;
      }

      // 4. Amount Spent filter
      if (minSpend && tx.amount < parseFloat(minSpend)) return false;
      if (maxSpend && tx.amount > parseFloat(maxSpend)) return false;

      // 5. VIP checkbox filter (from Amount Spent dropdown)
      if (vipOnly && tx.ticketType !== 'VIP') return false;

      return true;
    });
  }, [
    projectTicketTransactions,
    ticketFilter,
    ticketSearch,
    dateFrom,
    dateTo,
    minSpend,
    maxSpend,
    vipOnly,
  ]);

  const filteredTicketTransactions = useMemo(() => {
    if (project?.uuid) {
      // Sort live API transactions client-side
      return [...parsedLiveTransactions].sort((a, b) => {
        const dateA = new Date(a.rawDateTime).getTime();
        const dateB = new Date(b.rawDateTime).getTime();

        switch (sortBy) {
          case 'Newest':
            return dateB - dateA;
          case 'Oldest':
            return dateA - dateB;
          case 'Highest Amount':
            return b.amount - a.amount;
          case 'Lowest Amount':
            return a.amount - b.amount;
          default:
            return 0;
        }
      });
    } else {
      // Sort mock transactions client-side
      return [...mockFilteredTransactions].sort((a, b) => {
        const dateA = parseToLocalDate(a.dateTime).getTime();
        const dateB = parseToLocalDate(b.dateTime).getTime();

        switch (sortBy) {
          case 'Newest':
            return dateB - dateA;
          case 'Oldest':
            return dateA - dateB;
          case 'Highest Amount':
            return b.amount - a.amount;
          case 'Lowest Amount':
            return a.amount - b.amount;
          default:
            return 0;
        }
      });
    }
  }, [project?.uuid, parsedLiveTransactions, mockFilteredTransactions, sortBy]);

  const projectTasks = useMemo(() => {
    if (!project) return [];
    if (apiTasks && apiTasks.length > 0) {
      return apiTasks.map(mapApiTaskToLocalTask);
    }
    // Check if tasks exist in backend response (even if empty) to override local mock data
    if (apiTasksResponse) {
      return apiTasks.map(mapApiTaskToLocalTask);
    }
    return tasks.filter((t) => t.projectId === project.id);
  }, [project, apiTasks, apiTasksResponse, tasks]);

  // Run of Show
  const { data: scheduleResponse, isLoading: isScheduleLoading } = useGetRunOfShowEntries(
    { projectUuid: project?.uuid || '', page_size: 100 },
    { enabled: !!project?.uuid },
  );
  const projectScheduleSorted = useMemo(() => {
    const entries = scheduleResponse?.results ?? [];
    return [...entries].sort((a, b) => a.start_datetime.localeCompare(b.start_datetime));
  }, [scheduleResponse]);
  const scheduleGroupedByDate = useMemo(
    () => groupScheduleByDate(projectScheduleSorted),
    [projectScheduleSorted],
  );
  const { mutate: createScheduleEntryMutation, isPending: isCreatingSchedule } =
    useCreateRunOfShowEntry();
  const { mutate: updateScheduleEntryMutation, isPending: isUpdatingSchedule } =
    useUpdateRunOfShowEntry();
  const { mutate: deleteScheduleEntryMutation, isPending: isDeletingSchedule } =
    useDeleteRunOfShowEntry();

  const candidates = useMemo(
    () => importCandidatesResponse?.results ?? [],
    [importCandidatesResponse],
  );

  const filteredImportItems = useMemo(() => {
    if (!importSearchQuery.trim()) return candidates;
    const query = importSearchQuery.toLowerCase();
    return candidates.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        (item.category_label || item.category).toLowerCase().includes(query),
    );
  }, [candidates, importSearchQuery]);

  if (isProjectLoading) {
    return (
      <div className="min-h-screen bg-black relative pb-32">
        <Header />
        <PageLayout className="pt-[110px] animate-pulse">
          {/* Breadcrumbs skeleton */}
          <div className="flex items-center gap-4 mb-8">
            <div className="h-4 w-24 rounded bg-zinc-800" />
            <div className="h-4 w-3 rounded bg-zinc-800" />
            <div className="h-4 w-40 rounded bg-zinc-800" />
            <div className="flex-1" />
            <div className="h-8 w-28 rounded-full bg-zinc-800" />
          </div>
          {/* Title & subtitle skeleton */}
          <div className="mb-4">
            <div className="h-5 w-20 rounded-full bg-zinc-800 mb-3" />
            <div className="h-10 w-72 rounded bg-zinc-800 mb-2" />
            <div className="h-4 w-48 rounded bg-zinc-800" />
          </div>
          {/* Progress bar skeleton */}
          <div className="h-12 w-full max-w-md rounded-2xl bg-zinc-800/60 mb-8" />
          {/* Metric cards skeleton */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-28 rounded-2xl bg-zinc-800/40" />
            ))}
          </div>
          {/* Tabs skeleton */}
          <div className="flex items-center gap-2 border-b border-white/5 pb-4 mb-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-8 w-24 rounded-full bg-zinc-800/30" />
            ))}
          </div>
          {/* Content skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3 space-y-6">
              <div className="h-48 rounded-3xl bg-zinc-800/20" />
              <div className="h-64 rounded-3xl bg-zinc-800/20" />
            </div>
            <div className="lg:col-span-2 space-y-6">
              <div className="h-72 rounded-3xl bg-zinc-800/20" />
              <div className="h-48 rounded-3xl bg-zinc-800/20" />
            </div>
          </div>
        </PageLayout>
        <BottomNav />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-black relative pb-32">
        <Header />
        <PageLayout className="pt-[110px] text-center">
          <div className="py-20">
            <FiInfo className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white">Project Not Found</h2>
            <p className="text-zinc-500 mt-2">The project you are looking for does not exist.</p>
            <Link
              to="/projects"
              className="inline-block mt-6 px-6 py-2 bg-white text-black font-bold rounded-full text-xs"
            >
              Back to Projects
            </Link>
          </div>
        </PageLayout>
        <BottomNav />
      </div>
    );
  }

  // Permissions are the source of truth for view access
  // Guests with can_view: true should still be able to see the project
  if (project.permissions && !project.permissions.can_view) {
    return (
      <div className="min-h-screen bg-black relative pb-32">
        <Header />
        <PageLayout className="pt-[110px] text-center">
          <div className="py-20">
            <FiShield className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white">Access Restricted</h2>
            <p className="text-zinc-500 mt-2">You do not have permission to view this project.</p>
            <Link
              to="/projects"
              className="inline-block mt-6 px-6 py-2 bg-white text-black font-bold rounded-full text-xs"
            >
              Back to Projects
            </Link>
          </div>
        </PageLayout>
        <BottomNav />
      </div>
    );
  }

  // Budget data (including the "Budget" tab and metric cards) is hidden from
  // collaborators when the owner has switched off "Show Budget to Collaborators".
  const canSeeBudget = project.isMyProject || project.showBudgetToCollaborators;

  // Active Filter formatters
  const getOrdinalSuffix = (day: number) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  const formatDateLabel = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr + 'T00:00:00');
      const day = date.getDate();
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const month = months[date.getMonth()];
      return `${day}${getOrdinalSuffix(day)} ${month}`;
    } catch {
      return dateStr;
    }
  };

  const getDateFilterLabel = () => {
    if (dateFrom && dateTo) {
      return `${formatDateLabel(dateFrom)} - ${formatDateLabel(dateTo)}`;
    }
    if (dateFrom) {
      return `From ${formatDateLabel(dateFrom)}`;
    }
    if (dateTo) {
      return `To ${formatDateLabel(dateTo)}`;
    }
    return '';
  };

  const getAmountFilterLabel = () => {
    if (minSpend && maxSpend) {
      return `$${minSpend} - $${maxSpend}`;
    }
    if (minSpend) {
      return `Min $${minSpend}`;
    }
    if (maxSpend) {
      return `Max $${maxSpend}`;
    }
    return '';
  };

  // Project data subsets
  const projectMilestones = milestones.filter((m) => m.projectId === project.id);
  const projectCategories = budgetCategories.filter((c) => c.projectId === project.id);
  const projectTransactions = transactions.filter((t) => t.projectId === project.id);
  const apiProjectMembers = projectMembersData?.results ?? [];
  const apiPendingInvitations = (projectInvitationsData?.results ?? []).filter(
    (inv) => inv.status === 'pending',
  );

  // Adapt live members/invitations into the CrewMember row shape the Roles
  // tab UI already renders, so no JSX below needs to change.
  const projectCrew = [
    ...apiProjectMembers.map((m) => ({
      id: m.uuid,
      projectId: project.id,
      name: memberUserDisplayName(m.user),
      role:
        m.user.id === apiProject?.created_by_id
          ? 'Project Owner'
          : (ROLE_DISPLAY_LABEL[m.role] ?? m.role),
      status: 'Active' as const,
      description: m.user.email,
      initials: memberUserInitials(m.user),
      color: crewAvatarColor(m.user.id),
    })),
    ...apiPendingInvitations.map((inv) => ({
      id: inv.uuid,
      projectId: project.id,
      name: memberUserDisplayName(inv.invitee),
      role: ROLE_DISPLAY_LABEL[inv.role] ?? inv.role,
      status: 'Pending' as const,
      description: inv.email,
      initials: memberUserInitials(inv.invitee),
      color: 'bg-zinc-800',
      inviteSentDate: formatInviteDate(inv.created_at),
    })),
  ];
  const projectActivities = activities.filter((a: any) => a.projectId === project.id);
  const openTasks = projectTasks.filter((t) => t.status !== 'Complete');
  const inProgressTasksCount = projectTasks.filter((t) => t.status === 'In Progress').length;
  const projectExpenses = projectTransactions.filter((t) => t.type === 'Expense');
  const projectRevenues = projectTransactions.filter((t) => t.type === 'Revenue');
  const totalRevenue = projectRevenues.reduce((acc, curr) => acc + curr.amount, 0);

  const filteredTasksList = projectTasks.filter((t) => {
    if (taskFilter === 'All') return true;
    return t.status === taskFilter;
  });

  const ticketRevenue = project?.uuid
    ? parseFloat(String(ticketSalesData?.summary?.total_revenue ?? 0))
    : projectTicketTransactions.reduce((acc, tx) => acc + tx.amount, 0);

  const totalTicketsSold = project?.uuid
    ? (ticketSalesData?.summary?.total_tickets_sold ?? 0)
    : projectTicketTransactions.length;
  const totalSpentExpenses = project?.spentBudget ?? 0;
  const netProfitLoss = ticketRevenue - totalSpentExpenses;

  const budgetUsagePercent =
    project.allocatedBudget > 0
      ? Math.round((project.spentBudget / project.allocatedBudget) * 100)
      : 0;

  const handleAddInventoryItem = (e: React.FormEvent) => {
    e.preventDefault();
    if (!itemName.trim()) {
      setInventoryCreateError('Please enter an item name.');
      return;
    }
    if (!itemCategory || !itemCategory.trim()) {
      setInventoryCreateError('Please select a valid inventory category.');
      return;
    }

    let mappedCategory: string;
    try {
      mappedCategory = mapUiCategoryToApi(itemCategory);
    } catch (err: any) {
      setInventoryCreateError(extractApiError(err, 'Please select a valid inventory category.'));
      return;
    }

    setInventoryCreateError('');
    createInventoryItemMutation(
      {
        projectUuid: project.id,
        payload: {
          name: itemName,
          category: mappedCategory,
          asset_tag: itemAssetTag.trim() || undefined,
          condition: mapUiConditionToApi(itemCondition),
          budget_allocation: itemBudgetAllocation.trim() || undefined,
        },
      },
      {
        onSuccess: () => {
          setIsInventoryModalOpen(false);
          setItemName('');
          setItemCategory('');
          setItemAssetTag('');
          setItemQuantity('1');
          setItemBudgetAllocation('');
          setItemLocation('');
          setItemCondition('Excellent');
        },
        onError: (err: any) => {
          setInventoryCreateError(extractApiError(err, 'Failed to add inventory item'));
        },
      },
    );
  };

  const handleOpenEditInventoryModal = (item: any) => {
    setEditingInventoryItem(item);
    setEditItemName(item.name || '');
    setEditItemCategory(item.category || '');
    setEditItemAssetTag(item.assetTag || '');
    setEditItemQuantity(String(item.quantity || 1));
    setEditItemBudgetAllocation(item.budgetAllocation || '');
    setEditItemLocation(item.location || '');
    setEditItemCondition(item.condition || 'Excellent');
    setIsEditInventoryModalOpen(true);
  };

  const handleUpdateInventoryItem = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingInventoryItem) return;
    if (!editItemName.trim()) {
      setInventoryEditError('Please enter an item name.');
      return;
    }
    if (!editItemCategory || !editItemCategory.trim()) {
      setInventoryEditError('Please select a valid inventory category.');
      return;
    }

    let mappedCategory: string;
    try {
      mappedCategory = mapUiCategoryToApi(editItemCategory);
    } catch (err: any) {
      setInventoryEditError(extractApiError(err, 'Please select a valid inventory category.'));
      return;
    }

    setInventoryEditError('');
    updateInventoryItemMutation(
      {
        projectUuid: project.id,
        itemUuid: editingInventoryItem.id,
        payload: {
          name: editItemName,
          category: mappedCategory,
          asset_tag: editItemAssetTag.trim() || undefined,
          condition: mapUiConditionToApi(editItemCondition),
          budget_allocation: editItemBudgetAllocation.trim() || undefined,
        },
      },
      {
        onSuccess: () => {
          setIsEditInventoryModalOpen(false);
          setEditingInventoryItem(null);
        },
        onError: (err: any) => {
          setInventoryEditError(extractApiError(err, 'Failed to update inventory item'));
        },
      },
    );
  };

  const handleOpenDeleteInventoryModal = (item: any) => {
    setDeletingInventoryItem(item);
    setIsDeleteInventoryModalOpen(true);
  };

  const handleConfirmDeleteInventoryItem = () => {
    if (!deletingInventoryItem) return;
    deleteInventoryItemMutation(
      {
        projectUuid: project.id,
        itemUuid: deletingInventoryItem.id,
      },
      {
        onSuccess: () => {
          setIsDeleteInventoryModalOpen(false);
          setDeletingInventoryItem(null);
        },
      },
    );
  };

  const toggleImportSelection = (uuid: string) => {
    setSelectedImportIds((prev) =>
      prev.includes(uuid) ? prev.filter((id) => id !== uuid) : [...prev, uuid],
    );
  };

  const handleImportSelectedItems = () => {
    if (selectedImportIds.length === 0 || !project) return;
    importInventoryItemsMutation(
      {
        projectUuid: project.id,
        sourceItemUuuids: selectedImportIds,
      },
      {
        onSuccess: () => {
          setIsImportModalOpen(false);
          setSelectedImportIds([]);
        },
      },
    );
  };

  const handleExportTicketSales = () => {
    if (!filteredTicketTransactions || filteredTicketTransactions.length === 0) {
      toast.error('No ticket sales data available to export');
      return;
    }

    const headers = [
      'Customer Name',
      'Email',
      'Ticket Type',
      'Payment Method',
      'Amount ($)',
      'Quantity',
      'Date & Time',
    ];
    const rows = filteredTicketTransactions.map((tx) => [
      `"${(tx.customerName || '').replace(/"/g, '""')}"`,
      `"${(tx.customerEmail || '').replace(/"/g, '""')}"`,
      `"${(tx.ticketType || '').replace(/"/g, '""')}"`,
      `"${(tx.paymentMethod || '').replace(/"/g, '""')}"`,
      tx.amount,
      tx.quantity,
      `"${(tx.dateTime || '').replace(/"/g, '""')}"`,
    ]);

    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    const projectNameFormatted = project?.title
      ? project.title.toLowerCase().replace(/\s+/g, '_')
      : 'project';
    const fileName = `${projectNameFormatted}_ticket_sales_${new Date().toISOString().split('T')[0]}.csv`;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('Ticket sales data exported successfully!');
  };

  const resetScheduleForm = () => {
    setSchTitle('');
    setSchStartDate('');
    setSchStartTime('');
    setSchEndDate('');
    setSchEndTime('');
    setSchTimezone(DEFAULT_TIMEZONE);
    setSchLocation('');
    setSchAssigneeIds([]);
    setSchNotes('');
    setScheduleErrors({});
  };

  const openAddScheduleModal = () => {
    if (isGuestUser) {
      toast.error('You do not have permission to modify Run of Show.');
      return;
    }
    setEditingScheduleEntry(null);
    resetScheduleForm();
    setIsScheduleModalOpen(true);
  };

  const openEditScheduleModal = (entry: ApiRunOfShowEntry) => {
    if (isGuestUser) {
      toast.error('You do not have permission to modify Run of Show.');
      return;
    }
    if (!canUserUpdateScheduleEntry(entry)) {
      toast.error('You do not have permission to edit this schedule entry.');
      return;
    }
    const start = wallClockFromUtcIso(entry.start_datetime);
    const end = wallClockFromUtcIso(entry.end_datetime);
    setEditingScheduleEntry(entry);
    setSchTitle(entry.title);
    setSchStartDate(start.date);
    setSchStartTime(start.time);
    setSchEndDate(end.date);
    setSchEndTime(end.time);
    setSchTimezone(entry.iana_timezone || DEFAULT_TIMEZONE);
    setSchLocation(entry.location || '');
    setSchAssigneeIds((entry.assignees ?? []).map((a) => String(a.id)));
    setSchNotes(entry.notes || '');
    setScheduleErrors({});
    setIsScheduleModalOpen(true);
  };

  const validateScheduleEntry = (): boolean => {
    const errs: Record<string, string | undefined> = {};

    if (!schTitle.trim()) {
      errs.title = 'Title is required.';
    }

    if (!schStartDate || !schStartTime) {
      errs.startDateTime = 'Start date & time is required.';
    }

    if (!schEndDate || !schEndTime) {
      errs.endDateTime = 'End date & time is required.';
    }

    // iana_timezone is required whenever start and/or end date/time is populated.
    // Since start/end are themselves always required above, timezone is effectively
    // always required too — validated explicitly here in case it's ever cleared.
    if ((schStartDate || schStartTime || schEndDate || schEndTime) && !schTimezone) {
      errs.timezone = 'Timezone is required when a date/time is set.';
    }

    if (!errs.startDateTime && !errs.endDateTime && schTimezone) {
      const startISO = wallClockAsPicked(schStartDate, schStartTime);
      const endISO = wallClockAsPicked(schEndDate, schEndTime);
      if (endISO <= startISO) {
        errs.endDateTime = 'End time must occur after start time.';
      }
    }

    setScheduleErrors(errs);

    if (Object.keys(errs).length > 0) {
      toast.error(
        errs.title ||
          errs.startDateTime ||
          errs.endDateTime ||
          errs.timezone ||
          'Please correct the highlighted form errors.',
      );
      return false;
    }
    return true;
  };

  const handleScheduleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isGuestUser) {
      toast.error('You do not have permission to modify Run of Show.');
      setIsScheduleModalOpen(false);
      return;
    }
    if (!project?.uuid || isCreatingSchedule || isUpdatingSchedule) return;
    if (!validateScheduleEntry()) return;

    const payload = {
      title: schTitle.trim(),
      start_datetime: wallClockAsPicked(schStartDate, schStartTime),
      end_datetime: wallClockAsPicked(schEndDate, schEndTime),
      iana_timezone: schTimezone,
      // Always send location/notes (even "") rather than omitting them, so
      // clearing an optional field on edit actually clears it server-side
      // instead of leaving the PATCH body silently missing the key.
      location: schLocation.trim(),
      assignee_ids: schAssigneeIds.map((id) => Number(id)),
      notes: schNotes.trim(),
    };

    if (editingScheduleEntry) {
      updateScheduleEntryMutation(
        { projectUuid: project.uuid, entryUuid: editingScheduleEntry.uuid, payload },
        {
          onSuccess: () => {
            setIsScheduleModalOpen(false);
            setEditingScheduleEntry(null);
          },
        },
      );
    } else {
      createScheduleEntryMutation(
        { projectUuid: project.uuid, payload },
        { onSuccess: () => setIsScheduleModalOpen(false) },
      );
    }
  };

  const promptDeleteScheduleEntry = (entry: ApiRunOfShowEntry) => {
    if (isGuestUser) {
      toast.error('You do not have permission to modify Run of Show.');
      return;
    }
    if (!canUserDeleteScheduleEntry(entry)) {
      toast.error('You do not have permission to delete this schedule entry.');
      return;
    }
    setDeletingScheduleEntryId(entry.uuid);
  };

  const handleDeleteScheduleEntry = () => {
    if (isGuestUser) {
      toast.error('You do not have permission to modify Run of Show.');
      setDeletingScheduleEntryId(null);
      return;
    }
    if (!project?.uuid || !deletingScheduleEntryId) return;
    deleteScheduleEntryMutation(
      { projectUuid: project.uuid, entryUuid: deletingScheduleEntryId },
      { onSuccess: () => setDeletingScheduleEntryId(null) },
    );
  };

  const handleExportRunOfShow = async () => {
    if (projectScheduleSorted.length === 0) {
      toast.error('No schedule entries to export');
      return;
    }
    try {
      await exportRunOfShowPdf({
        projectTitle: project?.title || 'Project',
        projectDateRange:
          project?.startDate && project?.endDate
            ? `${project.startDate} – ${project.endDate}`
            : undefined,
        entries: projectScheduleSorted,
      });
    } catch {
      toast.error('Failed to export PDF');
    }
  };

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateAddTask() || isTaskSubmitting) return;

    setIsTaskSubmitting(true);
    let uploadedCoverUrl: string | null = null;
    let uploadedAttachmentUrls: string[] = [];

    try {
      if (coverFile) {
        const results = await uploadFilesToCloudinary([coverFile], {
          folder: 'task_images',
          uploadPreset: 'service_preset',
        });
        if (results[0]?.secure_url) {
          uploadedCoverUrl = results[0].secure_url;
        }
      }

      if (attachmentFile) {
        const results = await uploadFilesToCloudinary([attachmentFile], {
          folder: 'task_attachments',
          uploadPreset: 'service_preset',
        });
        if (results[0]?.secure_url) {
          uploadedAttachmentUrls = [results[0].secure_url];
        }
      }
    } catch (err) {
      console.error('Cloudinary upload error:', err);
      toast.error('Failed to upload file to Cloudinary. Please try again.');
      setIsTaskSubmitting(false);
      return;
    }

    const priorityMap: Record<'Low' | 'Medium' | 'High', ApiTaskPriority> = {
      Low: 'low',
      Medium: 'medium',
      High: 'high',
    };

    const statusMap: Record<'To Do' | 'In Progress' | 'Blocked' | 'Complete', ApiTaskStatus> = {
      'To Do': 'to_do',
      'In Progress': 'in_progress',
      Blocked: 'to_do',
      Complete: 'complete',
    };

    if (project?.uuid) {
      createTaskMutation(
        {
          projectUuid: project.uuid,
          payload: {
            title: taskTitle,
            description: taskDescription || '',
            cover_image: uploadedCoverUrl,
            attachments: uploadedAttachmentUrls,
            external_link: taskLink || '',
            priority: priorityMap[taskCreatePriority] || 'medium',
            status: statusMap[taskCreateStatus] || 'to_do',
            assignee_ids: taskAssigneeId
              ? [Number(taskAssigneeId)]
              : user?.id
                ? [Number(user.id)]
                : [],
            start_date: taskStartDate || null,
            end_date: taskEndDate || null,
            quantity: parseInt(taskQuantity, 10) || 1,
            budget_allocation: taskBudgetAllocation || '0.00',
          },
        },
        {
          onSuccess: () => {
            setIsTaskModalOpen(false);
            setTaskTitle('');
            setTaskDescription('');
            setTaskLink('');
            setCoverFile(null);
            setAttachmentFile(null);
            setCoverFileName('');
            setAttachmentFileName('');
            setTaskStartDate('');
            setTaskEndDate('');
            setTaskAssigneeId('');
            setTaskQuantity('1');
            setTaskBudgetAllocation('');
            setTaskCreatePriority('Medium');
            setTaskCreateStatus('To Do');
            setTaskErrors({});
            setIsTaskSubmitting(false);
          },
          onError: () => {
            setIsTaskSubmitting(false);
          },
        },
      );
      return;
    }

    const selectedMember = projectMembersData?.results?.find(
      (m: any) => String(m.user?.id || m.id) === taskAssigneeId,
    );
    const memberName = selectedMember
      ? selectedMember.user?.full_name || selectedMember.user?.username || 'Crew Member'
      : user?.full_name || user?.username || 'Unassigned';

    const nameParts = memberName.split(' ');
    const initials = nameParts.map((p: string) => p[0]).join('');

    addTask({
      projectId: project.id,
      title: taskTitle,
      priority: taskCreatePriority,
      status: taskCreateStatus === 'Blocked' ? 'To Do' : taskCreateStatus,
      assignee: {
        name: memberName,
        initials,
        color: 'bg-blue-600',
      },
      dueDate: taskEndDate || taskStartDate || '',
      description: taskDescription || undefined,
      externalLink: taskLink || undefined,
    });

    setIsTaskModalOpen(false);
    setTaskTitle('');
    setTaskDescription('');
    setTaskLink('');
    setCoverFileName('');
    setAttachmentFileName('');
    setTaskStartDate('');
    setTaskEndDate('');
    setTaskAssigneeId('');
    setTaskQuantity('1');
    setTaskBudgetAllocation('');
    setTaskCreatePriority('Medium');
    setTaskCreateStatus('To Do');
    setTaskErrors({});
    setIsTaskSubmitting(false);
  };

  const handleAddCategory = (e: React.FormEvent) => {
    e.preventDefault();
    if (!catName.trim()) return;

    const colors = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#22D3EE'];
    const chosenColor = colors[projectCategories.length % colors.length];

    addBudgetCategory({
      projectId: project.id,
      name: catName,
      allocated: parseFloat(catAllocated) || 0,
      dotColor: chosenColor,
      notes: catNotes || undefined,
    });

    setIsCategoryModalOpen(false);
    setCatName('');
    setCatNotes('');
  };

  const handleEditBudget = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCategory || !catEditName.trim()) return;

    updateBudgetCategory(editingCategory.id, {
      name: catEditName,
      allocated: parseFloat(catEditAllocated) || 0,
      notes: catEditNotes || undefined,
    });

    setIsEditBudgetModalOpen(false);
    setEditingCategory(null);
  };

  const handleAddTransaction = (e: React.FormEvent) => {
    e.preventDefault();
    if (!txTitle.trim()) return;

    const formatDateToHuman = (dateStr: string) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    };

    addTransaction({
      projectId: project.id,
      type: txType,
      title: txTitle,
      category: txCategory || (txType === 'Expense' ? 'Other' : 'Revenue'),
      amount: parseFloat(txAmount) || 0,
      date: formatDateToHuman(txDate) || 'Jul 7, 2026',
      status: 'Paid',
      notes: txNotes || undefined,
    } as any);

    setIsTxModalOpen(false);
    setTxTitle('');
    setTxCategory('');
    setTxNotes('');
  };

  return (
    <div className="min-h-screen bg-black relative pb-32">
      <Header />

      <PageLayout className="pt-[110px]">
        <ProjectHeaderBanner
          project={project}
          handleUnarchive={handleUnarchive}
          canSeeBudget={canSeeBudget}
          projectCrew={projectCrew}
          openTasks={openTasks}
          inProgressTasksCount={inProgressTasksCount}
          projectMilestones={projectMilestones}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />

        <div className="min-h-[400px]">
          {activeTab === 'Overview' && (
            <ProjectOverviewTab
              project={project}
              projectMilestones={projectMilestones}
              canSeeBudget={canSeeBudget}
              budgetUsagePercent={budgetUsagePercent}
              projectExpenses={projectExpenses}
              openTasks={openTasks}
              toggleTaskStatus={toggleTaskStatus}
              setActiveTab={setActiveTab}
            />
          )}

          {activeTab === 'Tasks' && (
            <ProjectTasksTab
              projectTasks={projectTasks}
              filteredTasksList={filteredTasksList}
              taskFilter={taskFilter}
              setTaskFilter={setTaskFilter}
              isGuestUser={isGuestUser}
              setIsTaskModalOpen={setIsTaskModalOpen}
              setDetailModalTask={setDetailModalTask}
              toggleTaskStatus={toggleTaskStatus}
              canUserUpdateTask={canUserUpdateTask}
              setStatusModalTask={setStatusModalTask}
              setSelectedStatus={setSelectedStatus}
              openEditTaskModal={openEditTaskModal}
              promptDeleteTask={promptDeleteTask}
              deletingTaskId={deletingTaskId}
            />
          )}

          {activeTab === 'Budget' && (
            <ProjectBudgetTab
              project={project}
              budgetUsagePercent={budgetUsagePercent}
              projectCategories={projectCategories}
              projectExpenses={projectExpenses}
              projectRevenues={projectRevenues}
              totalRevenue={totalRevenue}
              setIsCategoryModalOpen={setIsCategoryModalOpen}
              setEditingCategory={setEditingCategory}
              setCatEditName={setCatEditName}
              setCatEditAllocated={setCatEditAllocated}
              setCatEditNotes={setCatEditNotes}
              setIsEditBudgetModalOpen={setIsEditBudgetModalOpen}
              setTxType={setTxType}
              setIsTxModalOpen={setIsTxModalOpen}
            />
          )}

          {activeTab === 'Roles' && (
            <ProjectRolesTab
              project={project}
              projectCrew={projectCrew}
              projectActivities={projectActivities}
              user={user}
            />
          )}

          {activeTab === 'Inventory' && (
            <ProjectInventoryTab
              projectInventory={projectInventory}
              isLoading={isInventoryLoading}
              setIsInventoryModalOpen={setIsInventoryModalOpen}
              setIsImportModalOpen={setIsImportModalOpen}
              handleOpenEditInventoryModal={handleOpenEditInventoryModal}
              handleOpenDeleteInventoryModal={handleOpenDeleteInventoryModal}
            />
          )}

          {activeTab === 'Run of Show' && (
            <ProjectRunOfShowTab
              handleExportRunOfShow={handleExportRunOfShow}
              isGuestUser={isGuestUser}
              openAddScheduleModal={openAddScheduleModal}
              isScheduleLoading={isScheduleLoading}
              projectScheduleSorted={projectScheduleSorted}
              scheduleGroupedByDate={scheduleGroupedByDate}
              formatScheduleDuration={formatScheduleDuration}
              formatTimeOnly={(datetime, tz) => formatTimeOnly(datetime, tz, true)}
              formatTimezoneName={formatTimezoneName}
              canUserUpdateScheduleEntry={canUserUpdateScheduleEntry}
              canUserDeleteScheduleEntry={canUserDeleteScheduleEntry}
              openEditScheduleModal={openEditScheduleModal}
              promptDeleteScheduleEntry={promptDeleteScheduleEntry}
            />
          )}

          {activeTab === 'Activity' && <ProjectActivityTab projectActivities={projectActivities} />}

          {activeTab === 'Ticket Sales' && (
            <ProjectTicketSalesTab
              isGuestUser={isGuestUser}
              project={project}
              setSelectedEventUuid={setSelectedEventUuid}
              setSearchEventQuery={setSearchEventQuery}
              setIsChangeEventModalOpen={setIsChangeEventModalOpen}
              totalTicketsSold={totalTicketsSold}
              handleExportTicketSales={handleExportTicketSales}
              ticketRevenue={ticketRevenue}
              ticketTypesSummary={ticketTypesSummary}
              netProfitLoss={netProfitLoss}
              totalSpentExpenses={totalSpentExpenses}
              activePopover={activePopover}
              setActivePopover={setActivePopover}
              ticketFilter={ticketFilter}
              setTicketFilter={setTicketFilter}
              sortBy={sortBy}
              setSortBy={setSortBy}
              ticketSearch={ticketSearch}
              setTicketSearch={setTicketSearch}
              getAmountFilterLabel={getAmountFilterLabel}
              setMinSpend={setMinSpend}
              setMaxSpend={setMaxSpend}
              getDateFilterLabel={getDateFilterLabel}
              setDateFrom={setDateFrom}
              setDateTo={setDateTo}
              tempDateFrom={tempDateFrom}
              setTempDateFrom={setTempDateFrom}
              tempDateTo={tempDateTo}
              setTempDateTo={setTempDateTo}
              tempMinSpend={tempMinSpend}
              setTempMinSpend={setTempMinSpend}
              tempMaxSpend={tempMaxSpend}
              setTempMaxSpend={setTempMaxSpend}
              vipOnly={vipOnly}
              setVipOnly={setVipOnly}
              tempVipOnly={tempVipOnly}
              setTempVipOnly={setTempVipOnly}
              minSpend={minSpend}
              maxSpend={maxSpend}
              dateFrom={dateFrom}
              dateTo={dateTo}
              includeRefunded={includeRefunded}
              setIncludeRefunded={setIncludeRefunded}
              isSalesLoading={isSalesLoading}
              filteredTicketTransactions={filteredTicketTransactions}
              ticketSalesData={ticketSalesData}
              page={page}
              setPage={setPage}
            />
          )}
        </div>
      </PageLayout>

      {/* Task & Status Modals */}
      <TaskDetailsModal
        task={detailModalTask}
        onClose={() => setDetailModalTask(null)}
        onOpenEdit={(taskToEdit) => openEditTaskModal(taskToEdit)}
        onDelete={(taskId) => {
          const target =
            projectTasks.find((t) => t.id === taskId) ||
            apiTasks.find((t) => t.uuid === taskId) ||
            detailModalTask;
          promptDeleteTask(target);
        }}
        isDeleting={deletingTaskId === detailModalTask?.id}
        isGuestUser={isGuestUser}
      />

      <DeleteTaskModal
        task={taskToDelete}
        onConfirm={handleConfirmDeleteTask}
        onClose={() => setTaskToDelete(null)}
        isDeleting={!!deletingTaskId}
      />

      <UpdateStatusModal
        task={statusModalTask}
        selectedStatus={selectedStatus}
        onSelectStatus={(st) => setSelectedStatus(st)}
        onConfirm={handleConfirmUpdateStatus}
        onClose={() => setStatusModalTask(null)}
      />

      <EditTaskModal
        editingTask={editingTask}
        onClose={() => setEditingTask(null)}
        onSubmit={handleEditTask}
        isSubmitting={isTaskSubmitting}
        title={editTaskTitle}
        setTitle={setEditTaskTitle}
        description={editTaskDescription}
        setDescription={setEditTaskDescription}
        link={editTaskLink}
        setLink={setEditTaskLink}
        coverFileName={editCoverFileName}
        setCoverFile={setEditCoverFile}
        setCoverFileName={setEditCoverFileName}
        attachmentFileName={editAttachmentFileName}
        setAttachmentFile={setEditAttachmentFile}
        setAttachmentFileName={setEditAttachmentFileName}
        startDate={editStartDate}
        setStartDate={setEditStartDate}
        endDate={editEndDate}
        setEndDate={setEditEndDate}
        assigneeId={editAssigneeId}
        setAssigneeId={setEditAssigneeId}
        membersData={projectMembersData}
        quantity={editQuantity}
        setQuantity={setEditQuantity}
        budgetAllocation={editBudgetAllocation}
        setBudgetAllocation={setEditBudgetAllocation}
        priority={editPriority}
        setPriority={setEditPriority}
        status={editStatus}
        setStatus={setEditStatus}
        errors={editTaskErrors}
        setErrors={setEditTaskErrors}
      />

      <AddTaskModal
        isOpen={isTaskModalOpen}
        onClose={() => setIsTaskModalOpen(false)}
        onSubmit={handleAddTask}
        isSubmitting={isTaskSubmitting}
        title={taskTitle}
        setTitle={setTaskTitle}
        description={taskDescription}
        setDescription={setTaskDescription}
        link={taskLink}
        setLink={setTaskLink}
        coverFileName={coverFileName}
        setCoverFile={setCoverFile}
        setCoverFileName={setCoverFileName}
        attachmentFileName={attachmentFileName}
        setAttachmentFile={setAttachmentFile}
        setAttachmentFileName={setAttachmentFileName}
        startDate={taskStartDate}
        setStartDate={setTaskStartDate}
        endDate={taskEndDate}
        setEndDate={setTaskEndDate}
        assigneeId={taskAssigneeId}
        setAssigneeId={setTaskAssigneeId}
        membersData={projectMembersData}
        quantity={taskQuantity}
        setQuantity={setTaskQuantity}
        budgetAllocation={taskBudgetAllocation}
        setBudgetAllocation={setTaskBudgetAllocation}
        priority={taskCreatePriority}
        setPriority={setTaskCreatePriority}
        status={taskCreateStatus}
        setStatus={setTaskCreateStatus}
        errors={taskErrors}
        setErrors={setTaskErrors}
      />

      {/* Extracted Budget & Inventory & Settings Modals */}
      <AddBudgetCategoryModal
        isOpen={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
        onSubmit={handleAddCategory}
        catName={catName}
        setCatName={setCatName}
        catAllocated={catAllocated}
        setCatAllocated={setCatAllocated}
        catNotes={catNotes}
        setCatNotes={setCatNotes}
      />

      <EditBudgetCategoryModal
        isOpen={isEditBudgetModalOpen}
        editingCategory={editingCategory}
        onClose={() => {
          setIsEditBudgetModalOpen(false);
          setEditingCategory(null);
        }}
        onSubmit={handleEditBudget}
        catEditName={catEditName}
        setCatEditName={setCatEditName}
        catEditAllocated={catEditAllocated}
        setCatEditAllocated={setCatEditAllocated}
        catEditNotes={catEditNotes}
        setCatEditNotes={setCatEditNotes}
      />

      <AddTransactionModal
        isOpen={isTxModalOpen}
        onClose={() => setIsTxModalOpen(false)}
        onSubmit={handleAddTransaction}
        txType={txType}
        txTitle={txTitle}
        setTxTitle={setTxTitle}
        txAmount={txAmount}
        setTxAmount={setTxAmount}
        txDate={txDate}
        setTxDate={setTxDate}
        txCategory={txCategory}
        setTxCategory={setTxCategory}
        txNotes={txNotes}
        setTxNotes={setTxNotes}
        projectCategories={projectCategories}
      />

      <AddInventoryItemModal
        isOpen={isInventoryModalOpen}
        onClose={() => setIsInventoryModalOpen(false)}
        onSubmit={handleAddInventoryItem}
        itemName={itemName}
        setItemName={setItemName}
        itemCategory={itemCategory}
        setItemCategory={setItemCategory}
        isCategoryDropdownOpen={isCategoryDropdownOpen}
        setIsCategoryDropdownOpen={setIsCategoryDropdownOpen}
        itemAssetTag={itemAssetTag}
        setItemAssetTag={setItemAssetTag}
        itemQuantity={itemQuantity}
        setItemQuantity={setItemQuantity}
        itemBudgetAllocation={itemBudgetAllocation}
        setItemBudgetAllocation={setItemBudgetAllocation}
        itemLocation={itemLocation}
        setItemLocation={setItemLocation}
        itemCondition={itemCondition}
        setItemCondition={setItemCondition}
        isSubmitting={isCreatingInventory}
        error={inventoryCreateError}
      />

      <EditInventoryItemModal
        isOpen={isEditInventoryModalOpen}
        editingInventoryItem={editingInventoryItem}
        onClose={() => setIsEditInventoryModalOpen(false)}
        onSubmit={handleUpdateInventoryItem}
        editItemName={editItemName}
        setEditItemName={setEditItemName}
        editItemCategory={editItemCategory}
        setEditItemCategory={setEditItemCategory}
        isEditCategoryDropdownOpen={isEditCategoryDropdownOpen}
        setIsEditCategoryDropdownOpen={setIsEditCategoryDropdownOpen}
        editItemAssetTag={editItemAssetTag}
        setEditItemAssetTag={setEditItemAssetTag}
        editItemQuantity={editItemQuantity}
        setEditItemQuantity={setEditItemQuantity}
        editItemBudgetAllocation={editItemBudgetAllocation}
        setEditItemBudgetAllocation={setEditItemBudgetAllocation}
        editItemLocation={editItemLocation}
        setEditItemLocation={setEditItemLocation}
        editItemCondition={editItemCondition}
        setEditItemCondition={setEditItemCondition}
        isSubmitting={isUpdatingInventory}
        error={inventoryEditError}
      />

      <DeleteInventoryItemModal
        isOpen={isDeleteInventoryModalOpen}
        deletingInventoryItem={deletingInventoryItem}
        onClose={() => setIsDeleteInventoryModalOpen(false)}
        onConfirm={handleConfirmDeleteInventoryItem}
        isDeleting={isDeletingInventory}
      />

      <ImportAccountInventoryModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        importSearchQuery={importSearchQuery}
        setImportSearchQuery={setImportSearchQuery}
        selectedImportIds={selectedImportIds}
        setSelectedImportIds={setSelectedImportIds}
        filteredImportItems={filteredImportItems}
        toggleImportSelection={toggleImportSelection}
        handleImportSelectedItems={handleImportSelectedItems}
        isLoading={isImportCandidatesLoading}
        isSubmitting={isImportingInventory}
      />

      <ChangeLinkedEventModal
        isOpen={isChangeEventModalOpen}
        onClose={() => setIsChangeEventModalOpen(false)}
        project={project}
        searchEventQuery={searchEventQuery}
        setSearchEventQuery={setSearchEventQuery}
        filteredEvents={filteredEvents}
        selectedEventUuid={selectedEventUuid}
        setSelectedEventUuid={setSelectedEventUuid}
        onSave={(eventUuid) => {
          updateProjectMutation({
            projectUuid: project.id,
            payload: { linked_event_uuid: eventUuid || null },
          });
          setIsChangeEventModalOpen(false);
        }}
        onDetach={() => {
          updateProjectMutation({
            projectUuid: project.id,
            payload: { linked_event_uuid: null },
          });
          setIsChangeEventModalOpen(false);
        }}
      />

      <ScheduleEntryModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        onSubmit={handleScheduleSubmit}
        isSubmitting={isCreatingSchedule || isUpdatingSchedule}
        mode={editingScheduleEntry ? 'edit' : 'add'}
        title={schTitle}
        setTitle={setSchTitle}
        startDate={schStartDate}
        setStartDate={setSchStartDate}
        startTime={schStartTime}
        setStartTime={setSchStartTime}
        endDate={schEndDate}
        setEndDate={setSchEndDate}
        endTime={schEndTime}
        setEndTime={setSchEndTime}
        timezone={schTimezone}
        setTimezone={setSchTimezone}
        location={schLocation}
        setLocation={setSchLocation}
        assigneeIds={schAssigneeIds}
        setAssigneeIds={setSchAssigneeIds}
        membersData={projectMembersData}
        notes={schNotes}
        setNotes={setSchNotes}
        errors={scheduleErrors}
        setErrors={setScheduleErrors}
      />

      {deletingScheduleEntryId && (
        <DeleteScheduleEntryModal
          entryTitle={
            projectScheduleSorted.find((e) => e.uuid === deletingScheduleEntryId)?.title ||
            'Schedule Entry'
          }
          entrySubtitle={
            projectScheduleSorted.find((e) => e.uuid === deletingScheduleEntryId)?.location
          }
          onConfirm={handleDeleteScheduleEntry}
          onCancel={() => setDeletingScheduleEntryId(null)}
          isLoading={isDeletingSchedule}
        />
      )}
    </div>
  );
}
